<script lang="ts">
export default {
  name: 'Toast',
};
</script>

<script setup lang="ts">
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from '@lucide/vue';
import { useToast, type Toast } from '@/composables/useToast';
import { computed } from 'vue';
import { useHeaderOffset } from '@/composables/useHeaderOffset';

const { toasts, remove } = useToast();
const { totalOffset } = useHeaderOffset();

const toastTopStyle = computed(() => {
  const gap = 16;
  // Fall back to a typical header height until the header reports its measurement.
  return { top: `${(totalOffset.value || 64) + gap}px` };
});

const getToastIcon = (type: Toast['type']) => {
  switch (type) {
    case 'success':
      return CheckCircle;
    case 'error':
      return AlertCircle;
    case 'warning':
      return AlertTriangle;
    case 'info':
    default:
      return Info;
  }
};

const getToastClasses = (type: Toast['type']) => {
  const classes = ['alert'];
  
  switch (type) {
    case 'success':
      classes.push('alert-success');
      break;
    case 'error':
      classes.push('alert-error');
      break;
    case 'warning':
      classes.push('alert-warning');
      break;
    case 'info':
    default:
      classes.push('alert-info');
      break;
  }
  
  return classes.join(' ');
};
</script>

<template>
  <div 
    class="fixed right-4 z-1000 flex flex-col gap-2 max-w-md min-w-80"
    :style="toastTopStyle"
  >
    <div
      v-for="toast in toasts"
      :key="toast.id"
      :class="getToastClasses(toast.type)"
      class="shadow-lg"
    >
      <component :is="getToastIcon(toast.type)" class="w-5 h-5 shrink-0" />
      <div class="flex-1">
        <h3 v-if="toast.title" class="font-bold">{{ toast.title }}</h3>
        <div>{{ toast.message }}</div>
      </div>
      <button
        class="btn btn-sm btn-ghost btn-square"
        @click="remove(toast.id)"
      >
        <X class="w-4 h-4" />
      </button>
    </div>
  </div>
</template>
